import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Icon } from '../lib/icons';
import { useMediaQuery } from '../lib/useMediaQuery';
import { useDeployStatus } from '../lib/useDeployStatus';
import { APP_VERSION_LABEL } from '../lib/version';
import { matchRouteBack, matchRouteTitle, usePageChromeStore } from '../lib/pageChrome';
import { ActivityIndicator } from './activity/ActivityIndicator';
import { NotificationCenter } from './notifications/NotificationCenter';
import { SearchLauncher } from './search/SearchLauncher';
import { RequestCaptureHost } from './requests/RequestCaptureHost';
import { ReleaseBanner } from './ReleaseBanner';

/**
 * Slim, theme-aware app topbar (Unified Activity Center, Part A).
 *
 * Intended as the FIRST child of `<main class="app-main">` so it spans the
 * content area right of the fixed sidebar and sticks to the viewport top. Shown
 * at all widths (it unifies the legacy mobile-header). The integrator owns the
 * `.app-topbar` CSS and gates this behind the dark-launch flag in Layout.
 *
 * LEFT slot resolution order:
 *   (a) chrome store backTo/backLabel (runtime publisher, e.g. DetailHeader)
 *   (b) matchRouteBack(pathname) — route-table back affordance (works during
 *       loading/error, before any publisher mounts)
 *   (c) desktop → section title (ROUTE_TITLE); mobile → logo + app name
 * RIGHT slot: <ActivityIndicator/> (sibling slice).
 *
 * No backdrop-filter / transform on the bar — it must not become a containing
 * block that would trap the portaled Activity Center tray.
 */

/** Back affordance: a ghost button Link with the themed back glyph + label. When a
 *  publisher supplies `onBack` (e.g. the backlog detail's close-with-transition),
 *  render a button that runs it instead of plainly navigating to `backTo`. */
function BackAffordance({ backTo, backLabel, onBack }: { backTo: string; backLabel: string; onBack?: () => void }) {
  const content = (
    <>
      <Icon name="chevronRight" size={14} style={{ transform: 'rotate(180deg)', flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{backLabel}</span>
    </>
  );
  const style: React.CSSProperties = {
    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0,
  };
  if (onBack) {
    return (
      <button type="button" className="btn btn-ghost btn-sm" style={style} onClick={onBack}>
        {content}
      </button>
    );
  }
  return (
    <Link to={backTo} className="btn btn-ghost btn-sm" style={style}>
      {content}
    </Link>
  );
}

/** Mobile branding (mirrors the legacy Layout mobile-header markup). */
function MobileBrand() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
      <img src="/logo.svg" alt="" width={22} height={22} style={{ flexShrink: 0, borderRadius: '50%' }} />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
        <span style={{ fontWeight: 600, fontSize: 'var(--text-base)' }}>Personal Control Plane</span>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{APP_VERSION_LABEL}</span>
      </div>
    </div>
  );
}

/** Desktop section title for top-level pages. */
function SectionTitle({ title }: { title: string }) {
  return (
    <span
      style={{
        fontWeight: 600,
        fontSize: 'var(--text-base)',
        color: 'var(--text-primary)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
      }}
    >
      {title}
    </span>
  );
}

export function AppTopbar() {
  const { pathname } = useLocation();
  // Below 768px the sidebar is hidden and the legacy mobile-header showed
  // branding — match that threshold so the topbar shows logo + app name there
  // and the desktop section title above it.
  const isMobile = useMediaQuery('(max-width: 768px)');

  // While a deploy rolls, the bar grows a second (banner) row. The bar is no
  // longer height-pinned, so publish its true measured offsetHeight into the
  // single --topbar-height token every downstream offset already reads; clear
  // the inline override when idle so the CSS :root 52px default is restored.
  const barRef = useRef<HTMLDivElement>(null);
  const { inProgress } = useDeployStatus();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // The banner row stays mounted through its 200ms exit tween, so keep measuring
  // until AnimatePresence reports the exit is COMPLETE — not the instant
  // inProgress flips false. Otherwise --topbar-height would snap back to 52px
  // while the row is still visibly collapsing, leaving the trays/info-panel
  // briefly mis-offset over that window.
  const [bannerPresent, setBannerPresent] = useState(false);
  useEffect(() => { if (inProgress) setBannerPresent(true); }, [inProgress]);

  useLayoutEffect(() => {
    const bar = barRef.current;
    const root = document.documentElement;
    const clear = () => { if (root.style.getPropertyValue('--topbar-height')) root.style.removeProperty('--topbar-height'); };
    if (!bannerPresent || !bar || typeof ResizeObserver === 'undefined') {
      clear();
      return;
    }
    // Publish the bar's true height (content row + banner row + border + notch
    // inset, counted once) into the single token every downstream offset reads.
    // The observer re-fires through the enter/exit tween, so the offsets track
    // the variable, wrapping banner in lockstep.
    const publish = () => {
      const px = `${Math.round(bar.offsetHeight)}px`;
      if (root.style.getPropertyValue('--topbar-height') !== px) {
        root.style.setProperty('--topbar-height', px);
      }
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(bar);
    return () => { ro.disconnect(); clear(); };
  }, [bannerPresent]);

  // Runtime-published chrome wins (e.g. DetailHeader publishes the entity title
  // + its specific back target on load).
  const publishedBackTo = usePageChromeStore((s) => s.backTo);
  const publishedBackLabel = usePageChromeStore((s) => s.backLabel);
  const publishedOnBack = usePageChromeStore((s) => s.onBack);

  let left: React.ReactNode;
  if (publishedBackTo && publishedBackLabel) {
    // (a) a publisher set an explicit back affordance (+ optional onBack handler).
    left = <BackAffordance backTo={publishedBackTo} backLabel={publishedBackLabel} onBack={publishedOnBack} />;
  } else {
    const routeBack = matchRouteBack(pathname);
    if (routeBack) {
      // (b) route table resolves a back affordance — works before publishers mount.
      left = <BackAffordance backTo={routeBack.backTo} backLabel={routeBack.backLabel} />;
    } else if (isMobile) {
      // (c, mobile) logo + app name.
      left = <MobileBrand />;
    } else {
      // (c, desktop) section title for the current top-level page.
      const title = matchRouteTitle(pathname);
      left = title ? <SectionTitle title={title} /> : null;
    }
  }

  return (
    <div className="app-topbar" ref={barRef}>
      <div className="app-topbar-row">
        <div className="app-topbar-left" style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
          {left}
        </div>
        <div className="app-topbar-right" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', flexShrink: 0 }}>
          {/* Search sits LEFT of the bell: it is the thing you reach for on
              purpose, while the bell and activity report at you. (f-01826) */}
          <SearchLauncher />
          <NotificationCenter />
          <ActivityIndicator />
        </div>
      </div>
      {/* Headless: the New request sheet and the ⌘⇧F handler, mounted once so
          they are alive on every route. It renders no control of its own — the
          create buttons that already existed are what open it. (b-01933) */}
      <RequestCaptureHost />
      {/* Release-in-progress strip — second sticky row; subtle height/opacity
          expand on mount/unmount. The observer above re-measures during the
          tween so downstream offsets track the variable height in lockstep. */}
      <AnimatePresence initial={false} onExitComplete={() => setBannerPresent(false)}>
        {inProgress && (
          <motion.div
            key="release-row"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.2, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <ReleaseBanner />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
